import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrentUser } from '@/api/services/userService';
import { getApprovalWorkflowById } from '@/api/services/approvalWorkflowService';
import * as scenarioApi from '@/api/services/scenarioService';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  SendHorizontal,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Undo2,
  Target,
  UserPlus,
  X,
} from 'lucide-react';
import { ComprehensiveScenario, ScenarioShare, ScenarioStatus } from '../../types/scenarios';
import { useScenarios } from '../../hooks/useScenarios';
import { useApp } from '../../contexts/AppContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useUsers } from '../../contexts/UsersContext';
import { cn } from '../../lib/utils';

// ─── Workflow config ──────────────────────────────────────────────────────────
// Demo workflow — "Standard Approval" (ApprovalWorkflowsPage):
//   Stage 1 → Linda Kowalski  (user ID '4') — Finance Review
//   Stage 2 → Catherine Hughes (user ID '1') — CEO Sign-off
//
// Both stages are APPROVAL stages. There is no "review stage" in the formal
// workflow — under-review is the informal peer-sharing state before submission.
// Submission always enters the workflow at Stage 1 (pending-approval, order 1).
// Stage 1 approver advances to Stage 2; Stage 2 approver approves to Active.

const DEMO_WORKFLOW_STAGES: Record<number, string> = {
  1: '4',   // Stage 1 — Linda Kowalski (CFO)
  2: '1',   // Stage 2 — Catherine Hughes (CEO)
};

// Which statuses mean this scenario is "in flight" through the formal workflow
const IN_WORKFLOW_STATUSES: ScenarioStatus[] = [
  'pending-approval',
];

// ─── Status label helper ──────────────────────────────────────────────────────
const statusLabel = (status: ScenarioStatus): string => {
  const labels: Record<ScenarioStatus, string> = {
    draft: 'Draft',
    'under-review': 'Under Review',
    'pending-approval': 'Pending Approval',
    active: 'Active',
    committed: 'Committed',
    parked: 'Parked',
    rejected: 'Rejected',
    archived: 'Archived',
  };
  return labels[status] ?? status;
};

// ─── Status badge colour ──────────────────────────────────────────────────────
const statusBadgeClass = (status: ScenarioStatus): string => {
  switch (status) {
    case 'active':
    case 'committed':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'under-review':
    case 'pending-approval':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'rejected':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'parked':
    case 'archived':
      return 'bg-gray-100 text-gray-600 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

// ─── Initials helper ──────────────────────────────────────────────────────────
const getInitials = (name: string): string =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

// ─── Props ────────────────────────────────────────────────────────────────────
interface ScenarioWorkflowActionsProps {
  scenario: ComprehensiveScenario;
  /** 'card' = compact row of buttons; 'detail' = slightly larger with labels */
  variant?: 'card' | 'detail';
  onStatusChanged?: () => void;
}

// ─── Modals ───────────────────────────────────────────────────────────────────

interface SubmitModalProps {
  open: boolean;
  scenario: ComprehensiveScenario;
  onClose: () => void;
  /** skipReview: true = go straight to pending-approval; false = go to under-review */
  onConfirm: (note: string, skipReview: boolean) => void;
  onShare: (userId: string, userName: string) => void;
  onRemoveShare: (userId: string) => void;
}

const SubmitModal: React.FC<SubmitModalProps> = ({
  open,
  scenario,
  onClose,
  onConfirm,
  onShare,
  onRemoveShare,
}) => {
  const [note, setNote] = useState('');
  const [skipReview, setSkipReview] = useState(false);
  const { users } = useUsers();

  const handleClose = () => {
    setNote('');
    setSkipReview(false);
    onClose();
  };

  // Users who aren't already shared with and aren't the creator
  const availableToAdd = users.filter(
    (u) =>
      u.id !== scenario.createdBy &&
      !scenario.shares.some((s) => s.userId === u.id),
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SendHorizontal className="h-5 w-5" />
            Submit Scenario
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 leading-relaxed">
            Choose how to progress <strong className="text-gray-900">{scenario.name}</strong>.
          </DialogDescription>
        </DialogHeader>

        {/* Path selector */}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setSkipReview(false)}
            className={cn(
              'flex flex-col items-start gap-1.5 rounded-lg border-2 p-3 text-left transition-all',
              !skipReview
                ? 'border-[var(--accent-teal-500)] bg-[var(--accent-teal-50)]'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
            )}
          >
            <div className="flex items-center gap-1.5">
              <Target className="h-4 w-4" style={{ color: 'var(--accent-teal-500)' }} />
              <span className="text-sm font-semibold text-gray-900">Submit for Review</span>
            </div>
            <p className="text-xs text-gray-500 leading-snug">
              Share with reviewers first. You escalate to approval once reviewed.
            </p>
          </button>
          <button
            type="button"
            onClick={() => setSkipReview(true)}
            className={cn(
              'flex flex-col items-start gap-1.5 rounded-lg border-2 p-3 text-left transition-all',
              skipReview
                ? 'border-[var(--accent-teal-500)] bg-[var(--accent-teal-50)]'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
            )}
          >
            <div className="flex items-center gap-1.5">
              <SendHorizontal className="h-4 w-4" />
              <span className="text-sm font-semibold text-gray-900">Skip to Approval</span>
            </div>
            <p className="text-xs text-gray-500 leading-snug">
              Bypass the review stage and send directly to the approver.
            </p>
          </button>
        </div>

        {/* Shared-with list — only relevant when going for review */}
        {!skipReview && (
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-800">Shared with</Label>

            {scenario.shares.length === 0 ? (
              <p className="text-xs text-gray-400 italic py-1">No one is shared on this scenario yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {scenario.shares.map((share) => (
                  <li
                    key={share.userId}
                    className="flex items-center justify-between gap-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-1.5"
                  >
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-[10px]" style={{ backgroundColor: 'var(--accent-teal-50)', color: 'var(--accent-teal-500)' }}>
                          {getInitials(share.userName)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-gray-800">{share.userName}</span>
                      <span className="text-xs text-gray-400 capitalize">({share.permission})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRemoveShare(share.userId)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove access"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Add someone */}
            {availableToAdd.length > 0 && (
              <div className="pt-1">
                <Label className="text-xs text-gray-500 mb-1 block">Add someone</Label>
                <div className="flex flex-wrap gap-1.5">
                  {availableToAdd.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => onShare(u.id, u.name)}
                      className="flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2.5 py-1 text-xs text-gray-600 hover:border-[var(--accent-teal-500)] hover:text-[var(--accent-teal-500)] hover:bg-[var(--accent-teal-50)] transition-colors"
                    >
                      <UserPlus className="h-3 w-3" />
                      {u.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Covering note */}
        <div className="space-y-2">
          <Label htmlFor="submit-note" className="text-sm">
            Covering note <span className="text-gray-400 font-normal">(optional)</span>
          </Label>
          <Textarea
            id="submit-note"
            placeholder={skipReview ? 'Add any context for the approver…' : 'Add any context that will help the reviewer…'}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="resize-none"
          />
        </div>

        <DialogFooter className="gap-2 pt-1">
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button
            onClick={() => { onConfirm(note, skipReview); setNote(''); setSkipReview(false); }}
          >
            {skipReview ? (
              <><SendHorizontal className="h-4 w-4 mr-2" />Send for Approval</>
            ) : (
              <><Target className="h-4 w-4 mr-2" />Submit for Review</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface SendForApprovalModalProps {
  open: boolean;
  scenarioName: string;
  onClose: () => void;
  onConfirm: (note: string) => void;
}

const SendForApprovalModal: React.FC<SendForApprovalModalProps> = ({
  open,
  scenarioName,
  onClose,
  onConfirm,
}) => {
  const [note, setNote] = useState('');
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SendHorizontal className="h-5 w-5" />
            Send for Approval
          </DialogTitle>
          <DialogDescription>
            Send <strong>{scenarioName}</strong> into the approval workflow for final sign-off.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-1">
          <Label htmlFor="approval-note" className="text-sm">
            Covering note <span className="text-gray-400 font-normal">(optional)</span>
          </Label>
          <Textarea
            id="approval-note"
            placeholder="Add any context for the approver…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="resize-none"
          />
        </div>
        <DialogFooter className="gap-2 pt-1">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => { onConfirm(note); setNote(''); }}
          >
            <SendHorizontal className="h-4 w-4 mr-2" />
            Send for Approval
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface ApproveModalProps {
  open: boolean;
  scenarioName: string;
  isFinalApproval: boolean;
  onClose: () => void;
  onConfirm: (comment: string) => void;
}

const ApproveModal: React.FC<ApproveModalProps> = ({
  open,
  scenarioName,
  isFinalApproval,
  onClose,
  onConfirm,
}) => {
  const [comment, setComment] = useState('');
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            {isFinalApproval ? 'Final Approval' : 'Approve to Next Stage'}
          </DialogTitle>
          <DialogDescription>
            {isFinalApproval
              ? <>Approve <strong>{scenarioName}</strong>. This scenario will be marked Active and immediately available for planning use.</>
              : <>Approve <strong>{scenarioName}</strong> and pass it to the next stage for final sign-off.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Label htmlFor="approve-comment">Comment <span className="text-gray-400">(optional)</span></Label>
          <Textarea
            id="approve-comment"
            placeholder="Add an approval note for the audit trail…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { onConfirm(comment); setComment(''); }}>
            <CheckCircle2 className="h-4 w-4 mr-2" />
            {isFinalApproval ? 'Approve' : 'Approve & Forward'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface RejectModalProps {
  open: boolean;
  scenarioName: string;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

const RejectModal: React.FC<RejectModalProps> = ({ open, scenarioName, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const canSubmit = reason.trim().length > 0;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600" />
            Reject Scenario
          </DialogTitle>
          <DialogDescription>
            Reject <strong>{scenarioName}</strong> and return it to the creator for rework.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Label htmlFor="reject-reason">
            Reason for rejection <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="reject-reason"
            placeholder="Explain why this scenario needs rework…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
          {!canSubmit && reason.length > 0 && (
            <p className="text-xs text-red-500">A reason is required before rejecting.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="outline"
            disabled={!canSubmit}
            onClick={() => { onConfirm(reason); setReason(''); }}
          >
            <XCircle className="h-4 w-4 mr-2" />
            Reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface WithdrawModalProps {
  open: boolean;
  scenarioName: string;
  onClose: () => void;
  onConfirm: () => void;
}

const WithdrawModal: React.FC<WithdrawModalProps> = ({ open, scenarioName, onClose, onConfirm }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Undo2 className="h-5 w-5 text-orange-600" />
          Withdraw from Approval
        </DialogTitle>
        <DialogDescription>
          Withdraw <strong>{scenarioName}</strong> from the approval workflow. It will be returned
          to Draft status so you can make further changes before resubmitting.
        </DialogDescription>
      </DialogHeader>
      <div className="py-2">
        <p className="text-sm text-muted-foreground">
          Any approval progress will be reset. The approver will be notified that the submission
          has been withdrawn.
        </p>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button
          variant="outline"
          onClick={onConfirm}
        >
          <Undo2 className="h-4 w-4 mr-2" />
          Withdraw Submission
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ─── Main component ───────────────────────────────────────────────────────────

export const ScenarioWorkflowActions: React.FC<ScenarioWorkflowActionsProps> = ({
  scenario,
  variant = 'card',
  onStatusChanged,
}) => {
  const { currentUser, mode } = useApp();
  const queryClient = useQueryClient();
  const { data: currentUserData } = useQuery({
    queryKey: ['currentUser'],
    queryFn: getCurrentUser,
    enabled: mode === 'live',
    staleTime: 5 * 60 * 1000,
  });
  const { hasPermission } = usePermissions();
  const {
    requestApproval,
    approveScenario,
    rejectScenario,
    withdrawApproval,
    setStatus,
    markReviewed,
    shareScenario,
    removeShare,
  } = useScenarios();

  // Modal state
  const [submitOpen, setSubmitOpen] = useState(false);
  const [sendForApprovalOpen, setSendForApprovalOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  // ── Derived state ────────────────────────────────────────────────────────────

  const { status, createdBy, shares } = scenario;
  const isLive = mode === 'live';

  const effectiveUserId = mode === 'live' && currentUserData?.id ? currentUserData.id : currentUser.id;
  const isCreator = effectiveUserId === createdBy;

  // Is this user a non-creator share recipient?
  const isSharedWith = !isCreator && shares.some((s) => s.userId === effectiveUserId);

  // Workflow stage checks (demo mode only — keyed by user ID + currentStageOrder)
  // currentStageOrder tells us which stage the scenario is currently at.
  // We look up which user ID is the assignee for that stage in DEMO_WORKFLOW_STAGES.
  const currentStageOrder = scenario.currentStageOrder ?? 1;
  const expectedApproverForCurrentStage = DEMO_WORKFLOW_STAGES[currentStageOrder];
  const isCurrentStageApproverDemo =
    !isLive &&
    status === 'pending-approval' &&
    expectedApproverForCurrentStage === effectiveUserId;

  // Is this the final stage of the demo workflow?
  const totalDemoStages = Object.keys(DEMO_WORKFLOW_STAGES).length;
  const isFinalStageDemo = currentStageOrder >= totalDemoStages;

  const canApproveReject = hasPermission('approve_reject_scenarios');

  // ── Live mode: dynamic workflow resolution ──────────────────────────────────
  const isInApprovalWorkflow = isLive && scenario.approvalWorkflowId != null;

  // Fetch the approval workflow to check stage assignees (live mode only)
  const { data: approvalWorkflowData } = useQuery({
    queryKey: ['approvalWorkflow', scenario.approvalWorkflowId],
    queryFn: () => getApprovalWorkflowById(scenario.approvalWorkflowId!),
    enabled: isLive && scenario.approvalWorkflowId != null &&
             (status === 'under-review' || status === 'pending-approval'),
    staleTime: 5 * 60 * 1000,
  });

  // Check if the current user is an assignee of the current approval stage
  const isCurrentStageAssignee = (() => {
    if (!isLive || !approvalWorkflowData || scenario.currentStageId == null) return false;
    const currentStage = approvalWorkflowData.stages.find(
      (s) => Number(s.id) === scenario.currentStageId
    );
    if (!currentStage) return false;
    return currentStage.assignees.some((a) => a.userId === effectiveUserId);
  })();

  // ── Live mode button visibility ──────────────────────────────────────────────
  const showApproveRejectLive =
    isLive &&
    !isCreator &&
    canApproveReject &&
    status === 'pending-approval' &&
    isInApprovalWorkflow &&
    isCurrentStageAssignee;

  const showSubmitLive = isLive && isCreator && (status === 'draft' || status === 'rejected');
  const showWithdrawLive = isLive && isCreator && (status === 'under-review' || status === 'pending-approval');
  const showCreatorSendForApprovalLive = isLive && isCreator && status === 'under-review';
  // ── Demo mode button visibility ──────────────────────────────────────────────

  // Submit: creator only, from draft or rejected
  const showSubmitDemo = !isLive && isCreator && (status === 'draft' || status === 'rejected');
  const showResubmit = isCreator && status === 'rejected';

  // Withdraw: creator can recall from any in-flight state (under-review = informal; pending-approval = formal)
  const showWithdrawDemo = !isLive && isCreator && (status === 'under-review' || status === 'pending-approval');

  // Creator at under-review (informal peer review): can escalate into the formal approval workflow
  const showCreatorSendForApprovalDemo = !isLive && isCreator && status === 'under-review';
  const showCreatorSendForApproval = isLive ? showCreatorSendForApprovalLive : showCreatorSendForApprovalDemo;

  // Reviewer (shared, non-creator) at under-review: just a "Reviewed" confirmation — no formal action
  const hasAlreadyReviewed = scenario.approvalHistory?.some(
    a => a.userId === effectiveUserId && (a.status as string) === 'reviewed'
  ) ?? false;
  const alreadyReviewed = hasAlreadyReviewed || scenario.reviewedByUserId === effectiveUserId || scenario.reviewedBy != null;
  const showReviewedDemo = !isLive && isSharedWith && status === 'under-review' && !alreadyReviewed;
  const showReviewedLiveFiltered = isLive && isSharedWith && status === 'under-review' && !alreadyReviewed;
  const showReviewed = isLive ? showReviewedLiveFiltered : showReviewedDemo;

  // Current stage approver actions (demo mode):
  // Any assignee of the current stage can Approve/Reject, provided they are not the creator
  // (a creator cannot approve their own submission).
  // The approve button label and behaviour differ based on whether this is the final stage.
  const showApproveRejectDemo =
    !isLive &&
    !isCreator &&
    isCurrentStageApproverDemo &&
    canApproveReject;

  // Unified flags
  const showSubmit = isLive ? showSubmitLive : showSubmitDemo;
  const showWithdraw = isLive ? showWithdrawLive : showWithdrawDemo;

  // Show the "Reviewed" indicator even when no buttons are visible
  const showReviewedIndicator = !showReviewed && alreadyReviewed && isSharedWith && status === 'under-review';

  // Nothing to show
  const hasAnyAction =
    showSubmit ||
    showWithdraw ||
    showCreatorSendForApproval ||
    showReviewed ||
    showReviewedIndicator ||
    showApproveRejectLive ||
    showApproveRejectDemo;

  if (!hasAnyAction) return null;

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleSubmit = (_note: string, skipReview: boolean) => {
    if (isLive) {
      requestApproval(scenario.id, [], { skipReview, coveringNote: _note || undefined });
    } else if (skipReview) {
      // Skip informal review — enter the formal workflow at Stage 1 directly
      requestApproval(scenario.id, [DEMO_WORKFLOW_STAGES[1]]);
    } else {
      // Send for informal peer review first (under-review).
      // Creator will then escalate to formal approval via "Send for Approval".
      setStatus(scenario.id, 'under-review');
    }
    setSubmitOpen(false);
    onStatusChanged?.();
  };

  const handleShare = (userId: string, userName: string) => {
    shareScenario(scenario.id, { userId, userName, permission: 'view' });
  };

  const handleRemoveShare = (userId: string) => {
    removeShare(scenario.id, userId);
  };

  const handleCreatorSendForApproval = (_note: string) => {
    if (isLive) {
      // Live mode: call dedicated escalation endpoint
      const numericId = parseInt(scenario.id, 10);
      scenarioApi.escalateToApproval(numericId, { coveringNote: _note || undefined })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['scenarios'] });
          queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] });
          onStatusChanged?.();
        })
        .catch((error: unknown) => {
          console.error("Failed to escalate to approval:", error);
        });
    } else {
      // Demo: Creator escalates from informal review into the formal workflow at Stage 1
      requestApproval(scenario.id, [DEMO_WORKFLOW_STAGES[1]]);
      onStatusChanged?.();
    }
    setSendForApprovalOpen(false);
  };

  const handleReviewed = async () => {
    await markReviewed(scenario.id);
    onStatusChanged?.();
  };

  // Unified approve handler for demo mode — works for any stage
  const handleApproveDemo = (comment: string) => {
    approveScenario(scenario.id, comment);
    setApproveOpen(false);
    onStatusChanged?.();
  };

  const handleApproveLive = (comment: string) => {
    approveScenario(scenario.id, comment);
    setApproveOpen(false);
    onStatusChanged?.();
  };

  const handleReject = (reason: string) => {
    rejectScenario(scenario.id, reason);
    setRejectOpen(false);
    onStatusChanged?.();
  };

  const handleWithdraw = () => {
    withdrawApproval(scenario.id);
    setWithdrawOpen(false);
    onStatusChanged?.();
  };

  // ── Rejection callout (shown on card when rejected, creator only) ─────────────
  const rejectionCallout = isCreator && status === 'rejected' && (
    <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-lg mb-2">
      <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
      <p className="text-xs text-red-700">
        This scenario was rejected. Review the feedback in the Discussion tab and resubmit.
      </p>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  const btnSize = variant === 'card' ? 'sm' : 'default';

  return (
    <>
      {rejectionCallout}

      {/* Stage progress indicator — live mode */}
      {isLive && isInApprovalWorkflow && status === 'pending-approval' && scenario.currentStageName && (
        <div className="text-xs text-muted-foreground mb-1">
          {/^Stage\s+\d+$/i.test(scenario.currentStageName)
            ? `Stage ${scenario.currentStageOrder ?? 1}`
            : scenario.currentStageName}
        </div>
      )}

      {/* Stage progress indicator — demo mode */}
      {!isLive && status === 'pending-approval' && scenario.currentStageName && (
        <div className="text-xs text-muted-foreground mb-1">
          {/^Stage\s+\d+$/i.test(scenario.currentStageName)
            ? `Stage ${scenario.currentStageOrder ?? 1} of ${totalDemoStages}`
            : `${scenario.currentStageName} (${scenario.currentStageOrder ?? 1} of ${totalDemoStages})`}
        </div>
      )}

      <div className={cn('flex flex-wrap gap-2 justify-end', variant === 'detail' && 'mt-1')}>

        {/* Submit (Send for Review) — creator from draft/rejected */}
        {showSubmit && (
          <Button
            size={btnSize}
            onClick={(e) => { e.stopPropagation(); setSubmitOpen(true); }}
          >
            <Target className="h-3 w-3 mr-1.5" />
            {showResubmit ? 'Resubmit' : 'Submit for Review'}
          </Button>
        )}

        {/* Withdraw — creator pulls back from any in-flight state */}
        {showWithdraw && (
          <Button
            size={btnSize}
            variant="outline"
            onClick={(e) => { e.stopPropagation(); setWithdrawOpen(true); }}
          >
            <Undo2 className="h-3 w-3 mr-1.5" />
            Withdraw
          </Button>
        )}

        {/* Creator at under-review: escalate to approval */}
        {showCreatorSendForApproval && (
          <Button
            size={btnSize}
            onClick={(e) => { e.stopPropagation(); setSendForApprovalOpen(true); }}
          >
            <SendHorizontal className="h-3 w-3 mr-1.5" />
            Send for Approval
          </Button>
        )}

        {/* Reviewer (shared, non-creator) at under-review: just confirm reviewed */}
        {showReviewed && (
          <Button
            size={btnSize}
            variant="secondary"
            onClick={(e) => { e.stopPropagation(); handleReviewed(); }}
          >
            <Target className="h-3 w-3 mr-1.5" />
            Mark as Reviewed
          </Button>
        )}

        {/* Reviewed indicator — shown after reviewer has marked as reviewed */}
        {showReviewedIndicator && (
          <span className="text-xs text-green-600 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> Reviewed
          </span>
        )}

        {/* Live mode: approve/reject (unified — backend determines stage) */}
        {showApproveRejectLive && (
          <>
            <Button
              size={btnSize}
              onClick={(e) => { e.stopPropagation(); setApproveOpen(true); }}
            >
              <CheckCircle2 className="h-3 w-3 mr-1.5" />
              Approve
            </Button>
            <Button
              size={btnSize}
              variant="outline"
              onClick={(e) => { e.stopPropagation(); setRejectOpen(true); }}
            >
              <XCircle className="h-3 w-3 mr-1.5" />
              Reject
            </Button>
          </>
        )}

        {/* Demo: current stage approver — Approve/Reject at pending-approval */}
        {showApproveRejectDemo && (
          <>
            <Button
              size={btnSize}
              onClick={(e) => { e.stopPropagation(); setApproveOpen(true); }}
            >
              <CheckCircle2 className="h-3 w-3 mr-1.5" />
              {isFinalStageDemo ? 'Final Approve' : 'Approve & Forward'}
            </Button>
            <Button
              size={btnSize}
              variant="outline"
              onClick={(e) => { e.stopPropagation(); setRejectOpen(true); }}
            >
              <XCircle className="h-3 w-3 mr-1.5" />
              Reject
            </Button>
          </>
        )}

      </div>

      {/* Modals */}
      <SubmitModal
        open={submitOpen}
        scenario={scenario}
        onClose={() => setSubmitOpen(false)}
        onConfirm={handleSubmit}
        onShare={handleShare}
        onRemoveShare={handleRemoveShare}
      />
      <SendForApprovalModal
        open={sendForApprovalOpen}
        scenarioName={scenario.name}
        onClose={() => setSendForApprovalOpen(false)}
        onConfirm={handleCreatorSendForApproval}
      />
      <ApproveModal
        open={approveOpen}
        scenarioName={scenario.name}
        isFinalApproval={isLive ? (scenario.isFinalStage ?? true) : isFinalStageDemo}
        onClose={() => setApproveOpen(false)}
        onConfirm={isLive ? handleApproveLive : handleApproveDemo}
      />
      <RejectModal
        open={rejectOpen}
        scenarioName={scenario.name}
        onClose={() => setRejectOpen(false)}
        onConfirm={handleReject}
      />
      <WithdrawModal
        open={withdrawOpen}
        scenarioName={scenario.name}
        onClose={() => setWithdrawOpen(false)}
        onConfirm={handleWithdraw}
      />

    </>
  );
};

// ─── Status badge (re-exported for use in ScenarioCard footer) ────────────────
export const ScenarioStatusBadge: React.FC<{ status: ScenarioStatus }> = ({ status }) => (
  <Badge variant="outline" className={cn('text-xs font-medium', statusBadgeClass(status))}>
    {statusLabel(status)}
  </Badge>
);
